import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  businessLogin,
  businessSignup,
  businessMe,
  getToken,
  clearToken,
} from "../api/client";

// Shared auth/session state for the business console. Hydrates from the backend
// on load (if a token is present) so a refresh keeps the member signed in.
// organization_id is never stored client-side as an authority — it's always
// re-derived server-side from business_members; here we only keep display fields.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setOrganization(null);
      setLoading(false);
      return;
    }
    try {
      const data = await businessMe();
      setUser(data.user || null);
      setOrganization(data.organization || null);
    } catch {
      // Token invalid/expired — the API layer already cleared it on 401.
      clearToken();
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const login = useCallback(async (credentials) => {
    const data = await businessLogin(credentials);
    setUser(data.user || null);
    setOrganization(data.organization || null);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const data = await businessSignup(payload);
    setUser(data.user || null);
    setOrganization(data.organization || null);
    return data;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    setOrganization(null);
  }, []);

  // patchOrganization merges display fields (e.g. name, subdomain, logoUrl) into
  // the cached organization after a settings/logo change, so the co-branded shell
  // updates immediately without a full /me round-trip.
  const patchOrganization = useCallback((fields) => {
    setOrganization((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  const value = {
    user,
    organization,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    refresh: hydrate,
    patchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

export default AuthContext;
